import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  getSubordinatesWeeklyCheckinTableData,
  selectSubordinatesWeeklyCheckinTableData,
  selectGetSubordinatesWeeklyCheckinTableDataStatus,
  selectUsersSubordinatesIds,
  getUserSpanOfControl,
  selectGetSpanOfControlStatus,
  selectWeeklyCheckInUserStatus,
  selectGetSubordinatesWeeklyCheckInActivityEmailReportsTableDataStatus,
  selectSubordinatesWeeklyCheckInActivityEmailReportsTableData,
  getSubordinatesWeeklyCheckInActivityEmailReportsTableData,
  getWeeklyCheckInCompanySettings,
} from "../slice";
import CheckInActivityTable from "./CheckInActivityTableContainer";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  selectAllCompanyUsersById,
  selectCurrentUserAccountId,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import { getPeopleOrPerson, getS } from "utils/helperFunctions";
import { trackFeatureViewed } from "utils/trackingFunctions";
import { selectTeamAndDepartmentLeadIdsForLoggedInUser } from "app/containers/AdminConsole/slice";

interface Props {
  fallbackBackButton?: React.ReactNode;
}

const SubordinatesWeeklyCheckinTable = ({ fallbackBackButton }: Props) => {
  const dispatch = useAppDispatch();
  const allWeeklyCheckInTableData = useAppSelector(
    selectSubordinatesWeeklyCheckinTableData
  );
  const fetchingWeeklyCheckInTableDataStatus = useAppSelector(
    selectGetSubordinatesWeeklyCheckinTableDataStatus
  );
  const weeklyCheckinEmailReportDataByTimeAndType = useAppSelector(
    selectSubordinatesWeeklyCheckInActivityEmailReportsTableData
  );
  const teams = useAppSelector(selectTeamsByTeamId);
  const users = useAppSelector(selectAllCompanyUsersById);
  const currentUserAccountId = useAppSelector(selectCurrentUserAccountId);
  const usersSubordinatesIds = useAppSelector(selectUsersSubordinatesIds);
  const getSpanOfControlStatus = useAppSelector(selectGetSpanOfControlStatus);
  const teamAndDepartmentLeaderOf = useAppSelector(
    selectTeamAndDepartmentLeadIdsForLoggedInUser
  );
  const fetchingWeeklyCheckInActivityEmailReportsTableDataStatus =
    useAppSelector(
      selectGetSubordinatesWeeklyCheckInActivityEmailReportsTableDataStatus
    );
  const [featureTracked, setFeatureTracked] = useState(false);
  const weeklyCheckInUserStatus = useAppSelector(selectWeeklyCheckInUserStatus);

  useEffect(() => {
    dispatch(getUserSpanOfControl());
    dispatch(getWeeklyCheckInCompanySettings());
  }, [dispatch]);

  useEffect(() => {
    if (getSpanOfControlStatus !== "succeeded" && featureTracked) return;
    const subordinatesCount = usersSubordinatesIds?.length ?? 0;
    const hasSubordinates = subordinatesCount > 1;
    trackFeatureViewed(
      `Weekly Check-In Activity Table (${
        hasSubordinates ? "Direct Reports" : "Individual Contributor"
      })`
    );
    setFeatureTracked(true);
  }, [getSpanOfControlStatus, featureTracked, usersSubordinatesIds]);

  const { subordinatesCount, teamsThatUserLeadsCount } = useMemo((): {
    subordinatesCount: number;
    teamsThatUserLeadsCount: number;
  } => {
    if (!currentUserAccountId) {
      return { subordinatesCount: 0, teamsThatUserLeadsCount: 0 };
    }
    let subordinatesCount = usersSubordinatesIds?.length ?? 0;

    let teamsThatUserLeadsCount = 0;

    const user = users[currentUserAccountId];
    user?.teamIds?.forEach((teamId) => {
      const team = teams[teamId];
      if (team?.teamLeadUserAccountIds?.includes(currentUserAccountId)) {
        teamsThatUserLeadsCount++;
      }
    });

    // We subtract 1 because the user is included in the subordinates count
    if (subordinatesCount) {
      subordinatesCount -= 1;
    }

    return { subordinatesCount, teamsThatUserLeadsCount };
  }, [currentUserAccountId, usersSubordinatesIds, users, teams]);

  const fetchCheckInCompletionTableData = useCallback(
    (params: { entityType: string; timeInterval: string }) =>
      dispatch(getSubordinatesWeeklyCheckinTableData(params)),
    [dispatch]
  );

  const fetchCheckInEmailReportsTableData = useCallback(
    (params: { reportType: string; timeInterval: string }) =>
      dispatch(
        getSubordinatesWeeklyCheckInActivityEmailReportsTableData(params)
      ),
    [dispatch]
  );

  const getHeaderDescription = () => {
    if (getSpanOfControlStatus === "loading") {
      return "";
    }
    if (!subordinatesCount && !teamsThatUserLeadsCount) {
      return "Showing your Check-In activity.";
    }
    const base =
      "Showing Check-In activity for everyone in your reporting structure, including yourself";
    if (!subordinatesCount) {
      return `${base} and ${teamsThatUserLeadsCount} team${getS(
        teamsThatUserLeadsCount
      )} you lead.`;
    }
    if (!teamsThatUserLeadsCount) {
      return `${base} and ${subordinatesCount} ${getPeopleOrPerson(
        subordinatesCount
      )} reporting to you.`;
    }
    return `${base}, ${subordinatesCount} ${getPeopleOrPerson(
      subordinatesCount
    )} reporting to you, and ${teamsThatUserLeadsCount} team${getS(
      teamsThatUserLeadsCount
    )} you lead.`;
  };

  const incompleteUserAccountIds = useMemo(() => {
    const incompleteUserAccountIds: number[] = [];
    weeklyCheckInUserStatus?.pendingUsers?.forEach((userAccountId) => {
      if (usersSubordinatesIds?.includes(userAccountId)) {
        incompleteUserAccountIds.push(userAccountId);
      }
    });
    return incompleteUserAccountIds;
  }, [usersSubordinatesIds, weeklyCheckInUserStatus]);

  return (
    <CheckInActivityTable
      getWeeklyCheckInTableDataStatus={fetchingWeeklyCheckInTableDataStatus}
      allWeeklyCheckInTableData={allWeeklyCheckInTableData}
      fetchCheckInCompletionTableData={fetchCheckInCompletionTableData}
      headerDescription={getHeaderDescription()}
      fallbackBackButton={fallbackBackButton}
      hideDepartmentDropdown={!teamAndDepartmentLeaderOf?.departments?.length}
      hideEntityDropdown={!teamsThatUserLeadsCount && !subordinatesCount}
      isIndividualContributor={!subordinatesCount}
      incompleteUserAccountIds={incompleteUserAccountIds}
      fetchCheckInEmailReportsTableData={fetchCheckInEmailReportsTableData}
      weeklyCheckinEmailReportDataByTimeAndType={
        weeklyCheckinEmailReportDataByTimeAndType
      }
      getWeeklyCheckInActivityEmailReportsTableDataStatus={
        fetchingWeeklyCheckInActivityEmailReportsTableDataStatus
      }
    />
  );
};

export default SubordinatesWeeklyCheckinTable;
